import React, { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { docLinkRoute } from '@aline/common';
import { DialogActions, DialogContent, Divider, Grid, IconButton } from '@mui/material';
import copy from 'copy-to-clipboard';
import { isEmpty } from 'ramda';
import { animated, useSpring } from 'react-spring';
import type { ShareDocumentDialogAction, ShareDocumentDialogState } from 'src/components';
import {
  AccountsAutocomplete,
  AlineAlert,
  AlineButton,
  PermissionRoleMenu,
  ShareDocumentDialogActionType,
  ShareDocumentDialogDetails,
  SmallCloseIconButton,
  Spacer,
} from 'src/components';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { LinkIcon } from 'src/components/icons/LinkIcon';
import { env, isDev } from 'src/config';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getDocumentPermissionRoles } from 'src/helpers';
import {
  useAlineMeasure,
  useGetDocumentSimple,
  useGetOrgMembers,
  useUpsertStorablePermissions,
} from 'src/hooks';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { accountTypenameToAccountType } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  alert: {
    marginBottom: theme.spacing(2),
  },
  // Box sizing border-box and a bit of padding is needed to prevent a weird horizontal scroll on the dialog content.
  content: {
    boxSizing: 'border-box',
    flexDirection: 'column',
    padding: theme.spacing(1),
    width: '100%',
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  linkIcon: {
    marginRight: theme.spacing(1),
  },
  permissionRoleMenu: {
    border: 'none',
    height: 30,
    margin: theme.spacing(0, 0, 0, 1),
  },
  sendButton: {
    marginLeft: theme.spacing(1),
  },
}));

interface ShareDocumentDialogBodyProps {
  closeDialog: () => void;
  dialogDispatch: React.Dispatch<ShareDocumentDialogAction>;
  dialogState: ShareDocumentDialogState;
  documentId: UUID;
  onClickBack?: (event: React.MouseEvent) => void;
  showBackButton?: boolean;
}

export function ShareDocumentDialogBody({
  closeDialog,
  dialogDispatch,
  dialogState,
  documentId,
  onClickBack = () =>
    dialogDispatch({
      payload: { autocompleteValue: [] },
      type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
    }),
  showBackButton,
}: ShareDocumentDialogBodyProps): JSX.Element | null {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const { copyDocLink } = useCopyDocLink();
  const currentUser = useSelectCurrentUser();
  const { document } = useGetDocumentSimple(documentId);
  const [setRef, measurements] = useAlineMeasure<HTMLDivElement>();
  const { loading, upsertStorablePermissions } = useUpsertStorablePermissions();
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });
  const { alert, autocompleteValue, message, notifyRecipients, permissionRole } = dialogState;
  const styles = useSpring({ height: measurements?.height ?? 0 });

  async function handleClickSend(): Promise<void> {
    const newPermissions: Apollo.UpsertStorablePermissionsMutationVariables['input']['permissions'] =
      autocompleteValue.map(option => {
        if (option.__typename === 'NewUser') {
          return {
            accountType: Apollo.AccountType.User,
            email: option.email,
            origin: Apollo.PermissionOrigin.User,
            role: permissionRole,
          };
        }

        return {
          accountId: option.id,
          accountType: accountTypenameToAccountType(option.__typename),
          origin: Apollo.PermissionOrigin.User,
          role: permissionRole,
        };
      });

    await upsertStorablePermissions({
      variables: {
        input: {
          message,
          notifyRecipients,
          permissions: newPermissions,
          storableId: documentId,
        },
      },
    });

    closeDialog();

    setTimeout(() => {
      const numUsers = newPermissions.length;
      const userSingularOrPlural = numUsers === 1 ? 'user' : 'users';

      dispatch({
        payload: {
          key: uuid4(),
          message: `Sent document successfully to ${numUsers} ${userSingularOrPlural}!`,
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }, 500);
  }

  if (!document) return null;

  // Filter out the owner from the accounts passed to the autocomplete.
  const accounts = currentUser?.org ? [...orgMembers, currentUser.org] : orgMembers;
  const filteredAccounts = accounts.filter(account => {
    const isDocumentOwner = account.id === document.owner.id;
    return !isDocumentOwner;
  });
  const autocompleteEmpty = isEmpty(autocompleteValue);
  const permissionRoles = getDocumentPermissionRoles();
  const isAuthorizedToShare =
    document.requesterRole === Apollo.PermissionRole.Editor ||
    currentUser?.id === document.owner.id;
  const isBackButtonActive = !autocompleteEmpty || showBackButton;

  return (
    <animated.div style={styles}>
      <div ref={setRef}>
        <DialogContent className={classes.content}>
          {alert?.showAlert && (
            <AlineAlert
              action={
                <SmallCloseIconButton
                  color="inherit"
                  onClick={() =>
                    dialogDispatch({
                      payload: { alert: null },
                      type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
                    })
                  }
                />
              }
              className={classes.alert}
              severity="info"
            >
              {alert.msg}
            </AlineAlert>
          )}
          <Grid
            container
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
            wrap="nowrap"
          >
            <AccountsAutocomplete
              accounts={filteredAccounts}
              disabled={!isAuthorizedToShare}
              value={autocompleteValue}
              onChange={(_event, options) => {
                // If the user clears out the autocomplete, reset some state shown here back to the default.
                if (!options.length)
                  return dialogDispatch({
                    payload: { autocompleteValue: [], message: '', notifyRecipients: true },
                    type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
                  });

                dialogDispatch({
                  payload: { autocompleteValue: options },
                  type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
                });
              }}
            />
            <PermissionRoleMenu
              MenuProps={{
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
                transformOrigin: { horizontal: 'right', vertical: 'top' },
                variant: 'menu',
              }}
              border={false}
              className={classes.permissionRoleMenu}
              disabled={!isAuthorizedToShare}
              enabledRoles={permissionRoles}
              handleSelect={permissionRole =>
                dialogDispatch({
                  payload: { permissionRole },
                  type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
                })
              }
              value={permissionRole}
            />
          </Grid>
          <ShareDocumentDialogDetails
            autocompleteEmpty={autocompleteEmpty}
            dialogDispatch={dialogDispatch}
            dialogState={dialogState}
            disabled={!isAuthorizedToShare}
            document={document}
          />
        </DialogContent>
        <Divider className={classes.divider} variant="fullWidth" />
        <DialogActions>
          {isBackButtonActive && (
            <IconButton size="large" onClick={onClickBack}>
              <BackArrowIcon />
            </IconButton>
          )}
          <AlineButton
            className={classes.linkIcon}
            color="primary"
            variant="outlined"
            onClick={() => copyDocLink(documentId)}
          >
            <LinkIcon className={classes.linkIcon} />
            Copy Link
          </AlineButton>
          <Spacer />
          <AlineButton
            color="primary"
            disabled={!autocompleteEmpty && !isAuthorizedToShare}
            loading={loading}
            variant="contained"
            onClick={async () => {
              if (autocompleteEmpty) return closeDialog();

              await handleClickSend();
            }}
          >
            {autocompleteEmpty ? 'Done' : 'Send'}
          </AlineButton>
        </DialogActions>
      </div>
    </animated.div>
  );
}

export function useCopyDocLink() {
  const dispatch = useNotificationDispatch();
  const copyDocLink = useCallback(
    (documentId: UUID) => {
      copy(docLinkRoute(documentId, env), { debug: isDev });
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Document link copied to clipboard.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    },
    [dispatch]
  );

  return { copyDocLink };
}
